"use client";

import React, { useState, useEffect } from "react";
import { Box, Paper, Typography, Alert, Button, styled } from "@mui/material";
import moment from "moment";
import { decryptData } from "../cryptoUtils";

const TimeSlot = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginLeft: "-20px",
  paddingBottom: 5,
  minWidth: "40px", // Ensure time slots don't get too narrow
  maxWidth: "40px",
}));

const DayLabel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  borderBottom: "1px solid #e0e0e0",
  borderRight: "1px solid #e0e0e0",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  height: "100%", // Make sure it fills the height of its container
}));

//  ShiftBlock component
const ShiftBlock = styled(Paper)(({ theme, color, isDotted }) => ({
  backgroundColor: color,
  color: "white",
  padding: theme.spacing(1),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  border: isDotted ? "2px dashed #8A56E2" : "none",
  borderRadius: "4px",
  boxShadow: "none",
}));

// Current time indicator
const CurrentTimeIndicator = styled(Box)(({ theme }) => ({
  position: "absolute",
  width: "0px",
  borderRight: "1px dashed #FF5A5A",
  borderRightWidth: "2px",
  backgroundColor: "transparent",
  zIndex: 10,
}));

// Main component
export default function DailyShiftScheduler({ rota, UsersShifts }) {
  // Days for the schedule - full week starting from Friday

  const days = [
    {
      name: "Saturday",
      date: moment(Object.keys(rota.confirmedDates)[0]).format("DD MMMM"),
    },
    {
      name: "Sunday",
      date: moment(Object.keys(rota.confirmedDates)[1]).format("DD MMMM"),
    },
    {
      name: "Monday",
      date: moment(Object.keys(rota.confirmedDates)[2]).format("DD MMMM"),
    },
    {
      name: "Tuesday",
      date: moment(Object.keys(rota.confirmedDates)[3]).format("DD MMMM"),
    },
    {
      name: "Wednesday",
      date: moment(Object.keys(rota.confirmedDates)[4]).format("DD MMMM"),
    },
    {
      name: "Thursday",
      date: moment(Object.keys(rota.confirmedDates)[5]).format("DD MMMM"),
    },
    {
      name: "Friday",
      date: moment(Object.keys(rota.confirmedDates)[6]).format("DD MMMM"),
    },
  ];

  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedDays, setSelectedDays] = useState(() => {
    // Initialize with all days selected
    return days.map((day) => day.name);
  });
  const [currentTime, setCurrentTime] = useState(new Date());
  const scheduleRef = React.useRef(null);

  // Update current time every second for smoother movement
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second instead of every minute

    return () => clearInterval(interval);
  }, []);

  const handleDayToggle = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        // If all days would be deselected, keep this one selected
        if (prev.length === 1) return prev;
        // Otherwise remove this day
        return prev.filter((d) => d !== day);
      } else {
        // Add this day
        return [...prev, day];
      }
    });
  };

  // Time slots for the header - full 24 hours
  const timeSlots = [
    "00:00",
    "01:00",
    "02:00",
    "03:00",
    "04:00",
    "05:00",
    "06:00",
    "07:00",
    "08:00",
    "09:00",
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00",
    "20:00",
    "21:00",
    "22:00",
    "23:00",
    "24:00",
  ];

  function getShiftColor(startTime) {
    const hour = moment(startTime).hour(); // Get the hour of the start time
    if (hour >= 6 && hour < 14) {
      return "#8BC34A"; // Light Blue for Morning shift (6 AM - 2 PM)
    } else if (hour >= 14 && hour < 22) {
      return "#FF7F50"; // Coral for Afternoon shift (2 PM - 10 PM)
    } else {
      return "#2F4F4F"; // Dark Slate Gray for Night shift (10 PM - 6 AM)
    }
  }

  // Calculate current time indicator position
  const calculateCurrentTimePosition = () => {
    const hours = currentTime.getHours();

    const minutes = currentTime.getMinutes();

    const seconds = currentTime.getSeconds();

    // Calculate position with seconds precision for smoother movement
    const position = hours + minutes / 60 + seconds / 3600;
    const percentage = (position / 27) * 100;
    const gridIndex = Math.floor(position); // Grid number (0 to 23)

    return `${percentage}%`;
  };

  const currentTimePosition = calculateCurrentTimePosition();

  // Format current time for display
  const formatCurrentTime = () => {
    return currentTime.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Calculate position and width for shift blocks based on 24-hour format
  const calculateShiftPosition = (shift) => {
    const startHour = Number.parseInt(moment(shift.startTime).format("HH"));
    const startMinute = Number.parseInt(moment(shift.startTime).format("mm"));

    let endHour = Number.parseInt(moment(shift.endTime).format("HH"));
    const endMinute = Number.parseInt(moment(shift.endTime).format("mm"));

    // Handle shifts that go past midnight
    if (endHour < startHour) {
      endHour += 24;
    }

    const startPosition = startHour + startMinute / 60;
    const duration = endHour - startHour + (endMinute - startMinute) / 60;

    return {
      gridColumnStart: Math.max(1, Math.round(startPosition + 1)),
      gridColumnEnd: Math.round(startPosition + duration + 1),
    };
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        flex: 1,
        flexDirection: "column",
      }}
    >
      {/* Main content */}
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        {/* Day filter */}
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" fontWeight="medium" color="textSecondary">
            Filter by days:
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {days.map((day) => (
              <Button
                key={day.name}
                variant={
                  selectedDays.includes(day.name) ? "contained" : "outlined"
                }
                size="small"
                onClick={() => handleDayToggle(day.name)}
                sx={{
                  borderRadius: "20px",
                  bgcolor: selectedDays.includes(day.name)
                    ? "#0782b3"
                    : "transparent",
                  borderColor: "#0782b3",
                  color: selectedDays.includes(day.name) ? "white" : "#0782b3",
                  "&:hover": {
                    bgcolor: selectedDays.includes(day.name)
                      ? "#0782b3"
                      : "rgba(138, 86, 226, 0.1)",
                  },
                  textTransform: "none",
                  px: 2,
                  mb: 1,
                }}
              >
                {day.name}
              </Button>
            ))}
          </Box>
          <Button
            variant="text"
            size="small"
            onClick={() => setSelectedDays(days.map((day) => day.name))}
            sx={{
              color: "text.secondary",
              textTransform: "none",
            }}
          >
            Show All
          </Button>
        </Box>

        {/* Time slots header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(100px, auto) repeat(25, 1fr)",
          }}
        >
          {/* Empty corner cell */}
          <Box></Box>
          {/* Time slots */}
          {timeSlots.map((time, index) => (
            <TimeSlot key={index}>
              <Typography variant="body2" color="textSecondary">
                {time}
              </Typography>
            </TimeSlot>
          ))}
        </Box>
        {/* Schedule grid - with horizontal scroll for 24-hour view */}
        <Box
          sx={{
            overflow: "auto",
            maxWidth: "100%",
          }}
        >
          <Box
            ref={scheduleRef}
            sx={{
              border: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              minWidth: "1200px", // Ensure there's enough width for all 24 hours
            }}
          >
            {/* Current time indicator that spans the entire table */}
            {currentTimePosition && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: `calc(100px + ${currentTimePosition})`,
                  zIndex: 10,
                }}
              >
                <CurrentTimeIndicator
                  sx={{
                    height: "100%",
                    "&::before": {
                      content: `"${formatCurrentTime()}"`,
                      position: "absolute",
                      zIndex: 1,
                      top: "0px",
                      left: "-18px",
                      backgroundColor: "#FF5A5A",
                      color: "white",
                      padding: "2px 4px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              </Box>
            )}

            {/* Days and shifts */}
            {days
              .filter((day) => selectedDays.includes(day.name))
              .map((day, dayIndex) => {
                // Get shifts for this day
                const dayShifts = UsersShifts.filter(
                  (shift) =>
                    moment(shift.startTime).format("dddd").toString() ===
                    day.name
                );

                // Group shifts by time to determine how many rows we need
                const shiftRows = [];

                dayShifts.forEach((shift) => {
                  const position = calculateShiftPosition(shift);

                  // Find a row where this shift can fit
                  let rowIndex = 0;
                  let foundRow = false;

                  while (!foundRow && rowIndex < shiftRows.length) {
                    const row = shiftRows[rowIndex];
                    let canFit = true;

                    for (const existingShift of row) {
                      const existingStart = existingShift.gridColumnStart;
                      const existingEnd = existingShift.gridColumnEnd;

                      // Check if there's an overlap
                      if (
                        !(
                          position.gridColumnEnd <= existingStart ||
                          position.gridColumnStart >= existingEnd
                        )
                      ) {
                        canFit = false;
                        break;
                      }
                    }

                    if (canFit) {
                      foundRow = true;
                      row.push({
                        ...shift,
                        gridColumnStart: position.gridColumnStart,
                        gridColumnEnd: position.gridColumnEnd,
                      });
                    } else {
                      rowIndex++;
                    }
                  }

                  // If no existing row can fit this shift, create a new row
                  if (!foundRow) {
                    shiftRows.push([
                      {
                        ...shift,
                        gridColumnStart: position.gridColumnStart,
                        gridColumnEnd: position.gridColumnEnd,
                      },
                    ]);
                  }
                });

                return (
                  <Box
                    key={dayIndex}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(100px, auto) 1fr",
                      borderBottom:
                        dayIndex < days.length - 1
                          ? "1px solid rgba(224, 224, 224, 0.4)"
                          : "none",
                    }}
                  >
                    {/* Day label */}
                    <DayLabel>
                      <Typography variant="subtitle2" color="primary">
                        {day.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {day.date}
                      </Typography>
                    </DayLabel>

                    {/* Grid for shifts - auto height */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(25, 1fr)",
                        gridAutoRows: "minmax(70px, auto)",
                        gap: "8px",
                        padding: "8px",
                        position: "relative",
                      }}
                    >
                      {/* Vertical time dividers */}
                      {timeSlots.map((_, index) => (
                        <Box
                          key={index}
                          sx={{
                            position: "absolute",
                            left: `${((index + 1) / timeSlots.length) * 100}%`,
                            top: 0,
                            bottom: 0,
                            borderRight: "1px solid rgba(224, 224, 224, 0.29)",
                            zIndex: 0,
                          }}
                        />
                      ))}

                      {/* Shift blocks */}
                      {shiftRows.map((row, rowIndex) => (
                        <React.Fragment key={rowIndex}>
                          {row.map((shift) => (
                            <Box
                              key={shift.id}
                              sx={{
                                gridColumnStart: shift.gridColumnStart,
                                gridColumnEnd: shift.gridColumnEnd,
                                gridRow: rowIndex + 1,
                                zIndex: 1,
                              }}
                            >
                              <ShiftBlock
                                color={`${getShiftColor(shift.startTime)}`}
                                isDotted={false}
                              >
                                <Typography variant="caption">
                                  {moment(shift.startTime).format("HH:mm")} -{" "}
                                  {moment(shift.endTime).format("HH:mm")}
                                </Typography>
                                <Typography variant="body2">
                                  {shift.role}
                                </Typography>
                                <Typography variant="caption">
                                  {decryptData(
                                    shift.firstName,
                                    process.env.REACT_APP_EMP_KEY
                                  )}{" "}
                                  {decryptData(
                                    shift.lastName,
                                    process.env.REACT_APP_EMP_KEY
                                  )}
                                </Typography>
                              </ShiftBlock>
                            </Box>
                          ))}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
